import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { ErrorType, type FridgeState } from "types";
import type { LocalFridgeSegment } from "../types/mainTypes";
import { getShortenedFridgeId } from "../utils/utils";

type FridgeInfoSlice = {
	id: string | null;
	segments: LocalFridgeSegment[];
	location: string | null;
	state: FridgeState | null;
	fridgeErrorType: ErrorType | null;
};

const initialState: FridgeInfoSlice = {
	id: null,
	segments: [],
	location: null,
	state: null,
	fridgeErrorType: null,
};

export const fridgeInfoSlice = createSlice({
	name: "fridgeInfo",
	initialState: initialState,
	reducers: {
		setFridgeId: (state, { payload }: PayloadAction<string>) => {
			state.id = payload;
		},
		setSegments: (state, { payload }: PayloadAction<LocalFridgeSegment[]>) => {
			state.segments = payload;
		},
		setLocation: (state, { payload }: PayloadAction<string>) => {
			state.location = payload;
		},
		setFridgeState: (state, { payload }: PayloadAction<FridgeState | null>) => {
			state.state = payload;
		},
		setFridgeErrorType: (state, { payload }: PayloadAction<ErrorType | null>) => {
			state.fridgeErrorType = payload;
		},
	},
});

export const { setFridgeId, setSegments, setLocation, setFridgeState, setFridgeErrorType } = fridgeInfoSlice.actions;
export const selectFridgeId = (state: RootState) => state.fridgeInfo.id;

export const selectShortFridgeId = (state: RootState) => getShortenedFridgeId(state.fridgeInfo.id);
export const selectFridgeSegments = (state: RootState) => state.fridgeInfo.segments;
export const selectFridgeLocation = (state: RootState) => state.fridgeInfo.location;
export const selectFridgeState = (state: RootState) => state.fridgeInfo.state;
export const selectFridgeErrorType = (state: RootState) => state.fridgeInfo.fridgeErrorType;
export const selectHasNoActiveSalesPoint = (state: RootState) =>
	state.fridgeInfo.fridgeErrorType === ErrorType.CONFLICT;
// CONFLICT is excluded: it has its own dedicated modal; every other fridge error falls back to the
// generic connection-error screen ("for now" — until per-type screens exist).
export const selectHasFridgeError = (state: RootState) =>
	state.fridgeInfo.fridgeErrorType !== null && state.fridgeInfo.fridgeErrorType !== ErrorType.CONFLICT;

export default fridgeInfoSlice.reducer;
